<script setup>
import { ref, watch } from 'vue'
import { api } from '../../services/api'

// question = null nghĩa là thêm câu mới vào bài lessonId.
const props = defineProps({ question: { type: Object, default: null }, lessonId: { type: Number, required: true } })
const emit = defineEmits(['saved', 'cancel'])
const form = ref({})
const saving = ref(false)
const error = ref('')

watch(() => props.question, question => {
  form.value = question
    ? { type: question.type, difficulty: question.difficulty || 'basic', prompt: question.prompt, options: question.options?.length ? [...question.options] : ['', ''], correct_answer: question.correct_answer ?? '', explanation: question.explanation || '', points: question.points, rubric_text: (question.rubric || []).join(', ') }
    : { type: 'multiple_choice', difficulty: 'basic', prompt: '', options: ['', '', '', ''], correct_answer: '0', explanation: '', points: 1, rubric_text: '' }
  error.value = ''
}, { immediate: true })

function removeOption(index) {
  form.value.options.splice(index, 1)
  const correct = Number(form.value.correct_answer)
  if (correct === index) form.value.correct_answer = '0'
  else if (correct > index) form.value.correct_answer = String(correct - 1)
}

async function save() {
  if (saving.value) return
  const { rubric_text, ...rest } = form.value
  const options = rest.type === 'multiple_choice' ? rest.options.map(item => item.trim()) : []
  if (rest.type === 'multiple_choice' && (options.filter(Boolean).length < 2 || options.some(item => !item))) {
    error.value = 'Trắc nghiệm cần ít nhất 2 lựa chọn và không để trống lựa chọn nào.'
    return
  }
  saving.value = true
  error.value = ''
  try {
    const payload = { ...rest, options, rubric: rubric_text.split(',').map(item => item.trim()).filter(Boolean), points: Number(rest.points) || 1 }
    if (props.question) await api(`/admin/questions/${props.question.id}`, { method: 'PUT', body: JSON.stringify(payload) })
    else await api('/admin/questions', { method: 'POST', body: JSON.stringify({ ...payload, lesson_id: props.lessonId }) })
    emit('saved', props.question ? 'Đã lưu câu hỏi' : 'Đã thêm câu hỏi vào bài')
  } catch (err) { error.value = err.message }
  finally { saving.value = false }
}
</script>

<template>
  <form class="question-form" @submit.prevent="save">
    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>
    <div class="form-grid">
      <div class="form-group"><label>Dạng câu hỏi</label><select v-model="form.type" class="form-control" :disabled="saving"><option value="multiple_choice">Trắc nghiệm</option><option value="fill_blank">Điền đáp án</option><option value="essay">Tự luận</option></select></div>
      <div class="form-group"><label>Mức độ</label><select v-model="form.difficulty" class="form-control" :disabled="saving"><option value="basic">Cơ bản</option><option value="application">Vận dụng</option><option value="advanced">Nâng cao</option></select></div>
      <div class="form-group wide"><label>Nội dung câu hỏi</label><textarea v-model="form.prompt" class="form-control" required :disabled="saving"></textarea></div>
      <div v-if="form.type === 'multiple_choice'" class="form-group wide">
        <label>Các lựa chọn · bấm vòng tròn để chọn đáp án đúng</label>
        <div v-for="(option, index) in form.options" :key="index" class="option-line" :class="{ correct: form.correct_answer === String(index) }">
          <input v-model="form.correct_answer" type="radio" :value="String(index)" :aria-label="`Lựa chọn ${String.fromCharCode(65 + index)} là đáp án đúng`" :disabled="saving">
          <b>{{ String.fromCharCode(65 + index) }}</b>
          <input v-model="form.options[index]" class="form-control" :placeholder="`Lựa chọn ${String.fromCharCode(65 + index)}`" :disabled="saving">
          <button v-if="form.options.length > 2" type="button" class="option-remove" aria-label="Xóa lựa chọn" :disabled="saving" @click="removeOption(index)">×</button>
        </div>
        <button v-if="form.options.length < 6" type="button" class="text-link" :disabled="saving" @click="form.options.push('')">＋ Thêm lựa chọn</button>
      </div>
      <div v-else class="form-group wide"><label>{{ form.type === 'essay' ? 'Đáp án tham khảo' : 'Đáp án đúng' }}</label><input v-model="form.correct_answer" class="form-control" required :disabled="saving"></div>
      <div v-if="form.type === 'essay'" class="form-group wide"><label>Ý trọng tâm để gợi ý chấm · cách nhau bằng dấu phẩy</label><input v-model="form.rubric_text" class="form-control" :disabled="saving"></div>
      <div class="form-group wide"><label>Lời giải</label><textarea v-model="form.explanation" class="form-control" :disabled="saving"></textarea></div>
      <div class="form-group"><label>Điểm</label><input v-model.number="form.points" class="form-control" type="number" min="0.25" step="0.25" :disabled="saving"></div>
    </div>
    <p v-if="question" class="panel-hint">Nếu câu này đã có học sinh làm, hệ thống lưu thành phiên bản mới để bài làm cũ vẫn giữ nguyên.</p>
    <div class="panel-actions"><button class="btn btn-primary" :disabled="saving">{{ saving ? 'Đang lưu…' : question ? 'Lưu câu hỏi' : 'Thêm câu hỏi' }}</button><button type="button" class="btn btn-light" :disabled="saving" @click="emit('cancel')">Hủy</button></div>
  </form>
</template>

<style scoped>
.question-form { margin-top: 12px; padding: 16px; border: 1px solid #dcd7f5; border-radius: 14px; background: #fcfbff; }
.option-line { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; padding: 4px 6px; border-radius: 10px; }
.option-line.correct { background: #edf8f0; }
.option-line b { width: 16px; color: var(--muted); font-size: 12px; }
.option-line .form-control { height: 40px; }
.option-remove { flex: 0 0 30px; height: 30px; border: 0; border-radius: 8px; background: #f3f2f8; color: #8a8fa6; font-size: 17px; cursor: pointer; }
</style>
